import { MOTION, SHADOW } from "@elyxeer/ui";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import Typography from "@mui/material/Typography";
import { Link as RouterLink } from "react-router-dom";

import type { EventLinkState } from "~/components/events/event-origin";
import { EventStateBadge } from "~/components/events/EventStateBadge";
import { EventTime } from "~/components/events/EventTime";
import type { EventSummary } from "~/types/event";

/**
 * One event in a day's list. The whole card is the link, not just the title.
 *
 * `withDate` is for lists that are not already grouped under a day heading — the
 * upcoming strip, where each row has to say which day it belongs to.
 */
export function EventCard({
  event,
  withDate = false,
}: {
  event: EventSummary;
  withDate?: boolean;
}) {
  return (
    <Card
      sx={{
        transition: `box-shadow ${MOTION.transition}, transform ${MOTION.transition}`,
        "&:hover": { boxShadow: SHADOW.card, transform: "translateY(-2px)" },
      }}
    >
      <CardActionArea
        component={RouterLink}
        to={`/events/${event.slug}`}
        state={{ from: "calendar" } satisfies EventLinkState}
        data-testid={`events.card-${event.slug}`}
      >
        <Box
          sx={{
            padding: { xs: 2, md: 2.5 },
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <EventTime event={event} withDate={withDate} />
            <EventStateBadge state={event.state} />
          </Box>

          <Typography variant="H3" sx={{ color: "text.primary" }}>
            {event.title}
          </Typography>

          {event.summary ? (
            <Typography
              variant="T2"
              sx={{
                color: "text.secondary",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {event.summary}
            </Typography>
          ) : null}
        </Box>
      </CardActionArea>
    </Card>
  );
}
